import { useMemo, useState } from "react";
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Brain, CircleAlert, Filter, RefreshCw, Siren, Wallet } from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Funnel,
  FunnelChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import { useAdmin } from "../context/AdminContext";
import { formatRupiah } from "../lib/format";
import AdminLayout from "../components/AdminLayout";

const PERIOD_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "last_90_days", label: "Last 90 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
];

const COLORS = ["#0f766e", "#155e75", "#ea580c", "#be123c", "#6d28d9", "#334155"];

const Sparkline = ({ data = [] }) => (
  <ResponsiveContainer width="100%" height={36}>
    <LineChart data={data.map((v, i) => ({ i, v }))}>
      <Line type="monotone" dataKey="v" stroke="#0f766e" strokeWidth={2} dot={false} />
    </LineChart>
  </ResponsiveContainer>
);

const MetricCard = ({ item }) => {
  const up = item.comparison?.percent >= 0;
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
      <p className="text-xs text-stone-500">{item.label}</p>
      <p className="mt-1 text-xl font-semibold text-stone-900">
        {item.key.includes("rate") ? `${item.value.toFixed(2)}%` : formatRupiah(item.value)}
      </p>
      <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${up ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
        {up ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
        {Math.abs(item.comparison?.percent || 0).toFixed(2)}%
      </div>
      <div className="mt-2 h-9"><Sparkline data={item.sparkline} /></div>
    </div>
  );
};

const AdminDashboardPage = ({ staffToggle }) => {
  const { dashboardData, loading, error, refreshData, dashboardFilters, setDashboardFilters } = useAdmin();
  const [period, setPeriod] = useState(dashboardFilters?.period || "last_30_days");

  const executiveSummary = dashboardData?.executive_summary || [];
  const revenue = dashboardData?.revenue_analytics?.daily || [];
  const paymentMethods = useMemo(
    () => dashboardData?.payment_gateway_analytics?.revenue_by_payment_method || [],
    [dashboardData]
  );
  const topProducts = dashboardData?.profitability_analysis?.top_profitable_products || [];
  const lowMargin = dashboardData?.profitability_analysis?.lowest_margin_products || [];
  const insights = dashboardData?.ai_insights_center || [];
  const alerts = dashboardData?.alert_center || [];
  const funnelSteps = dashboardData?.sales_funnel?.steps || [];
  const cartAbandon = dashboardData?.cart_abandonment || {};
  const customer = dashboardData?.customer_analytics || {};
  const geo = dashboardData?.geographic_analytics?.revenue_by_location || [];
  const marketing = dashboardData?.marketing_performance?.revenue_sources || [];
  const inventory = dashboardData?.product_intelligence?.inventory_risk || {};
  const forecast = dashboardData?.forecasting || {};

  const paymentPie = useMemo(() => paymentMethods.map((p) => ({ name: p.method, value: p.revenue })), [paymentMethods]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-60 rounded bg-stone-200" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
              {Array.from({ length: 10 }).map((_, i) => <div key={i} className="h-36 rounded-2xl bg-stone-200" />)}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-rose-700">
            <p className="font-medium">Gagal memuat dashboard analytics</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <main className="mx-auto max-w-7xl space-y-6 px-4 py-8 sm:px-6 lg:px-8">
        <section className="rounded-3xl border border-stone-200 bg-gradient-to-br from-teal-900 via-cyan-900 to-slate-900 p-6 text-white shadow-xl">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="text-xs uppercase tracking-[0.22em] text-cyan-200">Executive Revenue Intelligence</p>
                {staffToggle && (
                  <button
                    onClick={() => staffToggle("operational")}
                    className="rounded-lg bg-white/15 px-3 py-1 text-xs font-medium text-cyan-100 hover:bg-white/25 transition-colors"
                  >
                    Tampilan Operasional →
                  </button>
                )}
              </div>
              <h1 className="mt-2 text-2xl font-semibold sm:text-3xl">Teras Alami Analytics Dashboard</h1>
              <p className="mt-1 text-sm text-cyan-100">Satu panel untuk monitor revenue, profit, conversion, retention, dan aksi prioritas.</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2 text-xs">
                <Filter className="h-4 w-4" />
                <select value={period} onChange={(e) => {
                  const next = e.target.value;
                  setPeriod(next);
                  setDashboardFilters((prev) => ({ ...prev, period: next }));
                }} className="bg-transparent text-white outline-none">
                  {PERIOD_OPTIONS.map((o) => <option className="text-black" key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <button onClick={refreshData} className="inline-flex items-center gap-2 rounded-xl bg-white/15 px-3 py-2 text-xs hover:bg-white/25">
                <RefreshCw className="h-4 w-4" /> Refresh
              </button>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
          {executiveSummary.map((item) => <MetricCard key={item.key} item={item} />)}
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm xl:col-span-2">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Revenue Trend (Gross, Discount, PG Fee, Net)</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenue}>
                  <defs>
                    <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0f766e" stopOpacity={0.4} /><stop offset="95%" stopColor="#0f766e" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="gross" stroke="#0f766e" fill="url(#g1)" />
                  <Area type="monotone" dataKey="discount" stroke="#ea580c" fill="transparent" />
                  <Area type="monotone" dataKey="pg_fee" stroke="#be123c" fill="transparent" />
                  <Area type="monotone" dataKey="net" stroke="#155e75" fill="transparent" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Revenue by Payment Method</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={paymentPie} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90}>
                    {paymentPie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Sales Funnel</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <FunnelChart>
                  <Tooltip />
                  <Funnel dataKey="count" data={funnelSteps} isAnimationActive />
                </FunnelChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Cart Abandonment & Recovery</h2>
            <div className="space-y-2 text-sm">
              <p>Abandoned Carts: <span className="font-semibold">{cartAbandon.abandoned_carts || 0}</span></p>
              <p>Abandonment Rate: <span className="font-semibold">{(cartAbandon.abandonment_rate || 0).toFixed(2)}%</span></p>
              <p>Potential Revenue Lost: <span className="font-semibold">{formatRupiah(cartAbandon.potential_revenue_lost || 0)}</span></p>
              <p>Recovery Rate: <span className="font-semibold">{(cartAbandon.recovery_performance?.recovery_rate || 0).toFixed(2)}%</span></p>
            </div>
            <div className="mt-5 h-40">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cartAbandon.daily_abandonment || []}>
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="abandoned" fill="#dc2626" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Top Profitable Products</h2>
            <div className="max-h-80 overflow-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="text-left text-stone-500">
                    <th className="py-2">Produk</th><th>Revenue</th><th>Cost</th><th>Discount</th><th>PG Fee</th><th>Profit</th><th>Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {topProducts.slice(0, 10).map((p) => (
                    <tr key={p.product_id} className="border-t border-stone-100">
                      <td className="py-2">{p.product}</td>
                      <td>{formatRupiah(p.revenue)}</td>
                      <td>{formatRupiah(p.cost)}</td>
                      <td>{formatRupiah(p.discount)}</td>
                      <td>{formatRupiah(p.pg_fee)}</td>
                      <td className="font-semibold">{formatRupiah(p.profit)}</td>
                      <td>{p.margin_percent}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Lowest Margin Products</h2>
            <div className="space-y-2">
              {lowMargin.slice(0, 8).map((p) => (
                <div key={p.product_id} className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                  <div>
                    <p className="text-sm font-medium text-stone-900">{p.product}</p>
                    <p className="text-xs text-stone-600">{p.warning}</p>
                  </div>
                  <p className="text-sm font-semibold text-amber-700">{p.margin_percent}%</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm xl:col-span-2">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Marketing Performance</h2>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={marketing}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="source" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="revenue" fill="#0f766e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold text-stone-900">Forecasting</h2>
            <div className="space-y-2 text-sm">
              <div className="rounded-xl bg-stone-50 p-3">
                <p className="text-stone-500">Revenue 30 Hari</p>
                <p className="text-lg font-semibold">{formatRupiah(forecast?.revenue?.next_30_days?.value || 0)}</p>
                <p className="text-xs text-stone-500">Confidence {forecast?.revenue?.next_30_days?.confidence || 0}%</p>
              </div>
              <div className="rounded-xl bg-stone-50 p-3">
                <p className="text-stone-500">Orders 30 Hari</p>
                <p className="text-lg font-semibold">{Math.round(forecast?.orders?.next_30_days?.value || 0)}</p>
                <p className="text-xs text-stone-500">Confidence {forecast?.orders?.next_30_days?.confidence || 0}%</p>
              </div>
              <div className="rounded-xl bg-stone-50 p-3">
                <p className="text-stone-500">Profit 30 Hari</p>
                <p className="text-lg font-semibold">{formatRupiah(forecast?.profit?.next_30_days?.value || 0)}</p>
                <p className="text-xs text-stone-500">Confidence {forecast?.profit?.next_30_days?.confidence || 0}%</p>
              </div>
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-900"><Brain className="h-4 w-4 text-cyan-600" /> AI Insights Center</h2>
            <div className="space-y-2">
              {insights.map((ins, idx) => (
                <div key={idx} className="rounded-xl border border-cyan-100 bg-cyan-50 p-3">
                  <p className="text-xs text-cyan-700">Priority: {ins.priority} | Impact: {ins.revenue_impact}</p>
                  <p className="mt-1 text-sm font-medium text-stone-900">{ins.message}</p>
                  <p className="text-xs text-stone-600">{ins.recommended_action}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-stone-900"><Siren className="h-4 w-4 text-rose-600" /> Alert Center</h2>
            <div className="space-y-2">
              {alerts.map((a, idx) => (
                <div key={idx} className={`rounded-xl border p-3 ${a.level === "critical" ? "border-rose-200 bg-rose-50" : a.level === "warning" ? "border-amber-200 bg-amber-50" : "border-blue-200 bg-blue-50"}`}>
                  <p className="flex items-center gap-2 text-xs font-medium uppercase">
                    {a.level === "critical" ? <AlertTriangle className="h-3.5 w-3.5" /> : a.level === "warning" ? <CircleAlert className="h-3.5 w-3.5" /> : <Wallet className="h-3.5 w-3.5" />}
                    {a.level}
                  </p>
                  <p className="mt-1 text-sm font-medium">{a.title}</p>
                  <p className="text-xs text-stone-600">{a.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-stone-900">Customer Analytics</h3>
            <p className="mt-2 text-sm">New: <span className="font-semibold">{customer.new_customers || 0}</span></p>
            <p className="text-sm">Returning: <span className="font-semibold">{customer.returning_customers || 0}</span></p>
            <p className="text-sm">CLV: <span className="font-semibold">{formatRupiah(customer.customer_lifetime_value || 0)}</span></p>
            <p className="text-sm">Repeat Rate: <span className="font-semibold">{(customer.repeat_purchase_rate || 0).toFixed(2)}%</span></p>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-stone-900">Inventory Risk</h3>
            <p className="mt-2 text-sm">Low Stock: <span className="font-semibold">{inventory.low_stock_products?.length || 0}</span></p>
            <p className="text-sm">Out Of Stock: <span className="font-semibold">{inventory.out_of_stock_products?.length || 0}</span></p>
            <p className="text-sm">Fast Selling: <span className="font-semibold">{inventory.fast_selling_products?.length || 0}</span></p>
          </div>
          <div className="rounded-2xl border border-stone-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-stone-900">Geographic Revenue Top 5</h3>
            <div className="mt-2 space-y-1 text-xs">
              {geo.slice(0, 5).map((g, idx) => <p key={idx}>{g.province}, {g.city}: <span className="font-semibold">{formatRupiah(g.revenue)}</span></p>)}
            </div>
          </div>
        </section>
      </main>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
